import {
  BaseTab,
  colors,
  getRecipeColor,
  IconButton,
  Spinner,
  Stack,
  Typography,
  useTabState,
} from '@ui-components/neo.tax';
import { ReactComponent as SupportTicketIcon } from '@ui-components/neo.tax/dist/icons/support-ticket.svg';

import { useAscContext } from '@neo.tax/asc/context';
import { useAscProject, useIsCapitalizedProjectClustering } from '@neo.tax/asc/hooks';
import {
  getPath,
  NavLinkTo,
  Path,
  useCurrentCompanyId,
  NavigateTo,
  useCompanyMembers,
  useHasSkippedConnectingTicketing,
  HIGHLIGHT_VARIANT,
} from '@neo.tax/shared';

import { AscProjectApprovalPane } from '../asc-project-approval-pane';
import { AscProjectDetailForm } from '../asc-project-detail-form';
import { AscProjectMatchingPane } from '../asc-project-matching-pane';
import { AscProjectTeamMembers } from '../asc-project-team-members';
import { DeleteAscProject } from '../delete-asc-project';
import {
  StyledBaseTabPanel,
  StyledBaseTabs,
  StyledCloseIcon,
  StyledDangerZone,
  StyledDivider,
  StyledStack,
  StyledTabPanelContainer,
  StyledAscTicketsPane,
} from './asc-project-detail.styled';
import { useAscProjectDetailTabIds } from './hooks';

interface AscProjectDetailProps {
  ascProjectId: string;
}

export const AscProjectDetail = ({ ascProjectId }: AscProjectDetailProps): React.ReactElement => {
  const companyId = useCurrentCompanyId();
  const { fiscalYearId, periodName } = useAscContext();
  const { hasSkippedConnectingTicketing } = useHasSkippedConnectingTicketing();
  const isCapitalizedProjectClustering = useIsCapitalizedProjectClustering();

  const { approvalTabId, detailTabId, matchingTabId, teamMembersTabId, ticketsTabId } = useAscProjectDetailTabIds();
  const tabState = useTabState({ defaultSelectedId: detailTabId });

  const { ascProject, loading } = useAscProject(ascProjectId);
  const members = useCompanyMembers(companyId);

  if (loading) {
    return (
      <Stack alignItems="center" paddingY={4}>
        <Spinner />
      </Stack>
    );
  }

  if (!ascProject) {
    return <NavigateTo to={getPath({ path: Path.ASC_PROJECTS, companyId, fiscalYearId, periodName })} />;
  }

  return (
    <StyledStack direction="column" overflow="hidden">
      <Stack
        direction="row"
        alignItems="start"
        justifyContent="space-between"
        paddingX={5}
        paddingY={5}
        backgroundColor={getRecipeColor(HIGHLIGHT_VARIANT, 'colorBg').bg}
      >
        <Stack direction="column" gap={1}>
          <Typography
            color={getRecipeColor(HIGHLIGHT_VARIANT, 'colorBg').highlightText}
            fontWeight="medium"
            fontSize="lg"
          >
            {ascProject.name}
          </Typography>
        </Stack>
        <NavLinkTo to={getPath({ path: Path.ASC_PROJECTS, companyId, fiscalYearId, periodName })}>
          <IconButton
            active
            variant="lightGray"
            icon={{
              node: <StyledCloseIcon />,
              isNotificationToggled: false,
            }}
            size="sm"
          />
        </NavLinkTo>
      </Stack>
      <StyledBaseTabs state={tabState} color={getRecipeColor(HIGHLIGHT_VARIANT, 'whiteBg').text}>
        <BaseTab label="Details" tabId={detailTabId} />
        {isCapitalizedProjectClustering ? <BaseTab label="Matching" tabId={matchingTabId} /> : <></>}
        <BaseTab label="Team" tabId={teamMembersTabId} />
        {hasSkippedConnectingTicketing ? <></> : <BaseTab label="Tickets" tabId={ticketsTabId} />}
        <BaseTab label="Approval" tabId={approvalTabId} />
      </StyledBaseTabs>
      <StyledTabPanelContainer>
        <StyledBaseTabPanel state={tabState} tabId={detailTabId}>
          <StyledStack direction="column" paddingX={4} paddingY={4}>
            <AscProjectDetailForm ascProject={ascProject} />
            <Stack direction="column" gap={6}>
              <Stack gap={2}>
                <SupportTicketIcon width={20} fill={colors.gray[400]} />
                <Typography fontSize="sm" fontWeight="medium">
                  {ascProject.ticketCount}
                </Typography>
                <Typography fontSize="sm">Tickets</Typography>
              </Stack>
            </Stack>
            {!ascProject.isDeterministic && (
              <StyledDangerZone direction="column" gap={3}>
                <StyledDivider />
                <DeleteAscProject ascProject={ascProject} />
              </StyledDangerZone>
            )}
          </StyledStack>
        </StyledBaseTabPanel>
        <StyledBaseTabPanel state={tabState} tabId={matchingTabId}>
          {isCapitalizedProjectClustering && <AscProjectMatchingPane ascProject={ascProject} />}
        </StyledBaseTabPanel>
        <StyledBaseTabPanel state={tabState} tabId={teamMembersTabId}>
          <AscProjectTeamMembers ascProject={ascProject} />
        </StyledBaseTabPanel>
        <StyledBaseTabPanel state={tabState} tabId={ticketsTabId}>
          <StyledAscTicketsPane ascProject={ascProject} />
        </StyledBaseTabPanel>
        <StyledBaseTabPanel state={tabState} tabId={approvalTabId}>
          <AscProjectApprovalPane ascProject={ascProject} members={members} />
        </StyledBaseTabPanel>
      </StyledTabPanelContainer>
    </StyledStack>
  );
};
